import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import type { Booking } from '../api/types';
import { ErrorMessage } from '../components/ErrorMessage';
import { PageLoader } from '../components/Loader';
import { MutationError } from '../components/MutationError';
import { useDebounced } from '../components/useDebounced';
import { fmtDateTime } from '../ops/format';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { bookingReason, leadLabel } from './labels';

// BookingClarification: Microsoft-Bookings-Buchung ohne eindeutige Zuordnung – der Support wählt den Lead (oder legt ihn
// aus der Buchung an); danach entsteht der Termin über die normale Terminlogik und die Rückfrage wird geschlossen.
export function BookingClarification({ ticketId }: { ticketId: string }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const bookings = useQuery({ queryKey: projectKeys.ticketBookings(project.id, ticketId), queryFn: ({ signal }) => papi.ticketBookings(ticketId, signal) });
  if (bookings.isPending) return <PageLoader label="Buchung wird geladen …" />;
  if (bookings.isError) return <ErrorMessage error={bookings.error} />;
  if (bookings.data.items.length === 0) return null;
  return (
    <section className="card" aria-labelledby="booking-title">
      <h2 id="booking-title">Buchung aus Microsoft Bookings</h2>
      {bookings.data.items.map((b) => (
        <BookingCard key={b.id} booking={b} ticketId={ticketId} />
      ))}
    </section>
  );
}

function BookingCard({ booking: b, ticketId }: { booking: Booking; ticketId: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [q, setQ] = useState(b.customer_phone ?? b.customer_email ?? b.customer_name);
  const [leadId, setLeadId] = useState('');
  const search = useDebounced(q, 300);
  const open = b.status === 'UNMATCHED' && !b.canceled_at && !b.appointment_id;
  const canAssign = open && can('ticket.manage') && can('appointment.create');
  const leads = useQuery({
    queryKey: projectKeys.leads(project.id, { q: search, booking: b.id }),
    queryFn: ({ signal }) => papi.leads({ q: search, limit: 10 }, signal),
    enabled: canAssign && search.trim().length >= 2,
  });
  const assign = useMutation({
    mutationFn: (body: { lead_id?: string; create_lead?: boolean }) => papi.assignBooking(b.id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.ticketBookings(project.id, ticketId) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.ticket(project.id, ticketId) });
    },
  });
  return (
    <div className="booking-card">
      <dl className="facts">
        <dt>Kunde</dt>
        <dd>{b.customer_name || '–'}</dd>
        <dt>Kontakt</dt>
        <dd>{[b.customer_email, b.customer_phone].filter(Boolean).join(' · ') || '–'}</dd>
        {b.customer_address && (
          <>
            <dt>Adresse</dt>
            <dd>{b.customer_address}</dd>
          </>
        )}
        <dt>Termin</dt>
        <dd>
          {fmtDateTime(b.start_at)} · {b.service_name || 'Buchung'}
          {b.staff_name && ` · bei ${b.staff_name}`}
        </dd>
        {b.customer_notes && (
          <>
            <dt>Hinweis</dt>
            <dd>{b.customer_notes}</dd>
          </>
        )}
        <dt>Stand</dt>
        <dd>
          {b.canceled_at ? 'In Bookings abgesagt' : b.appointment_id ? 'Zugeordnet' : 'In Klärung'}
          {bookingReason(b) && <span className="muted block">{bookingReason(b)}</span>}
        </dd>
      </dl>
      {b.appointment_id && (
        <p>
          <Link to={`/p/${project.key}/termine/${b.appointment_id}`}>Termin öffnen</Link>
          {b.lead_id && (
            <>
              {' · '}
              <Link to={`/p/${project.key}/leads/${b.lead_id}`}>{b.lead_name ?? 'Lead öffnen'}</Link>
            </>
          )}
        </p>
      )}
      {canAssign && (
        <div className="booking-assign">
          <label className="field wide">
            <span>Lead suchen (Name, Telefon, E-Mail)</span>
            <input value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          {leads.data && leads.data.items.length === 0 && <p className="muted">Kein Lead gefunden.</p>}
          {leads.data && leads.data.items.length > 0 && (
            <ul className="plain-list candidate-list" aria-label="Gefundene Leads">
              {leads.data.items.map((l) => (
                <li key={l.id}>
                  <label className="checkbox">
                    <input type="radio" name={`booking-lead-${b.id}`} checked={leadId === l.id} onChange={() => setLeadId(l.id)} /> {leadLabel(l)}
                  </label>
                </li>
              ))}
            </ul>
          )}
          <div className="form-actions">
            <button type="button" className="btn btn-primary" disabled={!leadId || assign.isPending} onClick={() => assign.mutate({ lead_id: leadId })}>
              Diesem Lead zuordnen
            </button>
            {can('lead.create') && (
              <button type="button" className="btn" disabled={assign.isPending} onClick={() => assign.mutate({ create_lead: true })}>
                Neuen Lead aus der Buchung anlegen
              </button>
            )}
          </div>
          {assign.isError && <MutationError error={assign.error} />}
        </div>
      )}
    </div>
  );
}
